import { questionsForAnswers } from "./groups";
import type {
  AnswerMap,
  AnswerValue,
  Condition,
  FormContent,
  Question,
  Section,
} from "./types";
import { isBlankEntry, isBlankScalarAnswer } from "./types";
import { parseMoney } from "./money";
import { byPos } from "./sortKey";
import { answerToText, hasOptions } from "./registry";

/**
 * Conditional logic, in two parts that do different jobs:
 *
 * - `visibleWhen` on a question or section — a predicate over the answers so
 *   far. Pure, no graph, nothing to get stuck in.
 * - section jumps — "if they answered No, go to section 4". A real graph, so
 *   it can loop and it can strand a section nobody reaches. `validateJumpGraph`
 *   below finds both, and publishing refuses a form that loops.
 */

/**
 * Whether an answer counts as "not given".
 *
 * The single notion of blankness: conditions (`isAnswered` / `isEmpty`),
 * the required check in `response.ts`, and anything counting answers must
 * agree on it, or a question can be simultaneously "answered" to one check
 * and "blank" to another.
 */
export function isEmptyValue(value: AnswerValue | undefined): boolean {
  if (!value) return true;
  switch (value.kind) {
    case "acknowledgment":
      return false;
    case "organization":
      return isBlankScalarAnswer(value);
    case "text":
      return value.value.trim() === "";
    case "choice":
      // A picked option is an answer; a blank "other" box is not.
      return value.choice.kind === "other" && value.choice.value.trim() === "";
    case "choices":
      // A lone blank "other" box is not a tick. Counting it would have
      // `isAnswered` and `required` disagree with the answer schema, which
      // already refuses one.
      return value.choices.every(
        (choice) => choice.kind === "other" && choice.value.trim() === "",
      );
    case "number":
      return false;
    case "entries":
      return value.entries.every(isBlankEntry);
    case "files":
      return value.files.length === 0;
    case "grid":
      // Any answered row counts as an answer; whether *every* row is answered
      // is the required check, not the empty check.
      return value.rows.length === 0;
  }
}

export function evaluateCondition(
  condition: Condition,
  answers: AnswerMap,
  questionsById: ReadonlyMap<string, Question>,
): boolean {
  const value = answers[condition.questionId];
  const question = questionsById.get(condition.questionId);

  switch (condition.op) {
    case "isAnswered":
      return !isEmptyValue(value);
    case "isEmpty":
      return isEmptyValue(value);
    default:
      break;
  }

  // A condition referencing a question that no longer exists never fires,
  // rather than throwing — a half-edited draft must still be previewable.
  if (!question) return false;

  const expectedText = condition.value ?? "";
  // Currency presentation must not change the amount a condition compares.
  // Normalize both sides so 25000, 25,000 and $25,000.00 mean the same thing.
  const expectedAmount =
    question.type === "money" ? parseMoney(expectedText) : undefined;
  const expected =
    expectedAmount === undefined ? expectedText : String(expectedAmount);

  // "The applicant took the Other box" — no value to compare, just which kind
  // of choice was made. A blank Other box is not a choice, same as everywhere
  // else that counts answers.
  if (condition.compare === "other") {
    const selected =
      value?.kind === "choices"
        ? value.choices
        : value?.kind === "choice"
          ? [value.choice]
          : [];
    const took = selected.some(
      (choice) => choice.kind === "other" && choice.value.trim() !== "",
    );
    switch (condition.op) {
      case "equals":
        return selected.length === 1 && took;
      case "notEquals":
        return !(selected.length === 1 && took);
      case "contains":
        return took;
    }
  }

  // On a choice question the condition holds an option *id*, not its label:
  // renaming an option must not quietly stop a condition from firing. Compare
  // identities, never the text — the text is display, and it moves.
  //
  // What the value means is taken from the condition, falling back to the
  // source's type only for conditions written before it was recorded.
  const comparesOptions =
    condition.compare === "option" ||
    (condition.compare === undefined && hasOptions(question.type));
  if (comparesOptions) {
    const selected =
      value?.kind === "choices"
        ? value.choices
        : value?.kind === "choice"
          ? [value.choice]
          : [];
    const ids = selected
      .filter((choice) => choice.kind === "option")
      .map((choice) => choice.optionId);
    switch (condition.op) {
      case "equals":
        return ids.length === 1 && ids[0] === expected;
      case "notEquals":
        return !(ids.length === 1 && ids[0] === expected);
      case "contains":
        return ids.includes(expected);
    }
  }

  // Compare a number against the number, not against its display text. A
  // linear scale renders as "3 out of 5", so a condition written as "3" — which
  // is what the builder stores — would never match, silently hiding whatever it
  // guards. `toText` is for display and storage, not for comparison.
  const moneyAmount =
    question.type === "money" && value?.kind === "text"
      ? parseMoney(value.value)
      : undefined;
  const text =
    question.type === "acknowledgment"
      ? value?.kind === "acknowledgment" && value.value === true
        ? "Acknowledged"
        : "Not acknowledged"
      : moneyAmount !== undefined
        ? String(moneyAmount)
        : value?.kind === "number"
          ? String(value.value)
          : value?.kind === "text" && question.type === "money"
            ? value.value
            : answerToText(question, value);
  switch (condition.op) {
    case "equals":
      return text === expected;
    case "notEquals":
      return text !== expected;
    case "contains":
      return text.includes(expected);
  }
}

/** Sections in display order. */
export function orderedSections(form: FormContent): Section[] {
  return [...form.sections].sort(byPos);
}

/** A section's questions, in display order. */
export function orderedQuestions(
  form: FormContent,
  sectionId: string,
): Question[] {
  return form.questions
    .filter((question) => question.sectionId === sectionId)
    .sort(byPos);
}

export function orderedAnswerQuestions(
  form: FormContent,
  sectionId: string,
): Question[] {
  return questionsForAnswers(form)
    .filter((q) => q.sectionId === sectionId)
    .sort(byPos);
}

/**
 * Every question in document order: sections by their sort key, then questions
 * by theirs.
 *
 * `pos` only orders questions *within* a section — two questions in different
 * sections routinely carry the same key — so comparing `pos` across sections is
 * meaningless. Anything that needs "which question comes first" must go through
 * here rather than comparing keys directly.
 */
export function questionsInFormOrder(form: FormContent): Question[] {
  const ordered = orderedSections(form).flatMap((section) =>
    orderedQuestions(form, section.id),
  );
  // Questions whose section is gone go last rather than being dropped. They
  // arise when a section is deleted while someone else is offline adding to it,
  // and silently discarding them would lose an admin's work — and hide the very
  // thing that blocks publishing. Applicants never see them: the renderer walks
  // sections, so an orphan is unreachable until it is re-homed.
  const placed = new Set(ordered.map((question) => question.id));
  const orphans = form.questions
    .filter((question) => !placed.has(question.id))
    .sort(byPos);
  return [...ordered, ...orphans];
}

/** Questions asked before this one, in document order. */
export function questionsBefore(
  form: FormContent,
  questionId: string,
): Question[] {
  const ordered = questionsInFormOrder(form);
  const index = ordered.findIndex((question) => question.id === questionId);
  const earlier = index === -1 ? ordered : ordered.slice(0, index);
  const repeats = new Set(
    (form.groups ?? []).filter((g) => g.repeat).map((g) => g.id),
  );
  const target = ordered[index];
  return target?.groupId && repeats.has(target.groupId)
    ? earlier.filter((q) => q.groupId === target.groupId)
    : earlier.filter((q) => !q.groupId || !repeats.has(q.groupId));
}

export function questionsById(form: FormContent): Map<string, Question> {
  return new Map(
    questionsForAnswers(form).map((question) => [question.id, question]),
  );
}

/**
 * Whether a question's own conditions hold.
 *
 * Not the same as "was it asked" — a section skipped by a jump still passes
 * this. Use `visibleQuestions` (or `askedQuestionIds`) for that; this exists
 * for rendering a single question whose section is already known to be on the
 * path.
 */
export function isQuestionVisible(
  question: Question,
  form: FormContent,
  answers: AnswerMap,
  byId = questionsById(form),
): boolean {
  const section = form.sections.find((s) => s.id === question.sectionId);
  if (
    section?.visibleWhen &&
    !evaluateCondition(section.visibleWhen, answers, byId)
  ) {
    return false;
  }
  if (!question.visibleWhen) return true;
  return evaluateCondition(question.visibleWhen, answers, byId);
}

/**
 * Every question actually asked, in order — what a submitted response covers.
 *
 * This is the single answer to "was this question asked": it walks the jump
 * path as well as evaluating conditions. Callers must not re-derive it from
 * `isQuestionVisible` alone, which knows nothing about jumps and would silently
 * disagree with validation.
 */
export function visibleQuestions(
  form: FormContent,
  answers: AnswerMap,
): Question[] {
  const byId = questionsById(form);
  const visited = new Set<string>();
  const result: Question[] = [];

  // Conditions are evaluated against answers to questions that are *still*
  // asked, accumulated as we walk. Evaluating against the raw answer map lets a
  // stale value drive later visibility: answer Q2, then change Q1 so Q2 is no
  // longer asked, and Q2's leftover answer would still reveal Q3 — which
  // validation then treats as hidden, so a reviewer sees an answer nobody was
  // asked for.
  const effective: AnswerMap = {};

  for (const section of walkSections(form, answers)) {
    if (visited.has(section.id)) continue;
    visited.add(section.id);
    for (const question of orderedAnswerQuestions(form, section.id)) {
      const asked = question.visibleWhen
        ? evaluateCondition(question.visibleWhen, effective, byId)
        : true;
      if (!asked) continue;
      result.push(question);
      const value = answers[question.id];
      if (value !== undefined) effective[question.id] = value;
    }
  }
  return result;
}

/**
 * The path an applicant takes through the sections, following jumps.
 *
 * Guarded against revisiting a section even though publishing rejects loops:
 * a draft being previewed hasn't been published yet.
 */
export function walkSections(form: FormContent, answers: AnswerMap): Section[] {
  return walkOutcome(form, answers).path;
}

export interface WalkOutcome {
  path: Section[];
  /**
   * Whether the walk reached a genuine end — an explicit "submit" jump or the
   * last section in order. False when it was cut short instead: a jump that
   * loops back to a visited section, or one pointing at a section that no
   * longer exists. The distinction matters to anyone offering a Submit
   * button: the last section of a truncated walk is where the applicant is
   * *stuck*, not where the form *ends* — publishing refuses these graphs, but
   * a draft being previewed hasn't been published yet.
   */
  complete: boolean;
}

/** The walk, plus whether it actually reached the form's end. */
export function walkOutcome(
  form: FormContent,
  answers: AnswerMap,
): WalkOutcome {
  const ordered = orderedSections(form);
  const byId = questionsById(form);
  const sectionById = new Map(ordered.map((s) => [s.id, s]));
  const path: Section[] = [];
  const seen = new Set<string>();

  // Only answers to questions still on the path may steer the walk. Evaluating
  // jumps against the raw answer map lets a leftover answer to a question that
  // is no longer asked skip a whole section — the same defect that let one
  // reveal a later question, one level up.
  const effective: AnswerMap = {};

  let current: Section | undefined = ordered[0];
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    const section: Section = current;
    const visible: boolean =
      !section.visibleWhen ||
      evaluateCondition(section.visibleWhen, effective, byId);
    if (visible) {
      path.push(section);
      for (const question of orderedAnswerQuestions(form, section.id)) {
        const asked = question.visibleWhen
          ? evaluateCondition(question.visibleWhen, effective, byId)
          : true;
        const value = answers[question.id];
        if (asked && value !== undefined) effective[question.id] = value;
      }
    }

    // A section the applicant never sees doesn't get to steer them. Its jumps
    // and its explicit next only apply when the section is actually shown;
    // otherwise the walk carries on in document order as though it weren't
    // there.
    const next: string | null | undefined = visible
      ? nextSectionId(section, ordered, effective, byId)
      : (ordered[ordered.findIndex((s) => s.id === section.id) + 1]?.id ??
        null);
    // `null` is the one genuine ending: an explicit submit, or the order
    // running out. Anything else that stops the loop — a dangling target, a
    // jump looping back to `seen` — cut the walk short.
    if (next === null) return { path, complete: true };
    current = next === undefined ? undefined : sectionById.get(next);
  }
  return { path, complete: false };
}

function nextSectionId(
  section: Section,
  ordered: readonly Section[],
  answers: AnswerMap,
  byId: ReadonlyMap<string, Question>,
): string | null | undefined {
  for (const jump of section.jumps ?? []) {
    if (evaluateCondition(jump.when, answers, byId)) return jump.toSectionId;
  }
  if (section.defaultNextSectionId !== undefined) {
    return section.defaultNextSectionId;
  }
  const index = ordered.findIndex((s) => s.id === section.id);
  const next = ordered[index + 1];
  return next ? next.id : null;
}

export interface JumpGraphProblem {
  kind: "cycle" | "unreachable" | "danglingTarget";
  sectionIds: string[];
  message: string;
}

/**
 * Check the section jump graph.
 *
 * A cycle **blocks** publishing: it's an applicant who can't finish and can't
 * tell you why. An unreachable section only warns — a question nobody is asked
 * is annoying, and obvious the first time someone tests the form.
 */
export function validateJumpGraph(form: FormContent): {
  errors: JumpGraphProblem[];
  warnings: JumpGraphProblem[];
} {
  const ordered = orderedSections(form);
  const ids = new Set(ordered.map((s) => s.id));
  const errors: JumpGraphProblem[] = [];
  const warnings: JumpGraphProblem[] = [];
  if (ordered.length === 0) return { errors, warnings };

  // Every edge a section can take, regardless of which condition fires.
  const edges = sectionEdges(form);
  for (const [index, section] of ordered.entries()) {
    for (const jump of section.jumps ?? []) {
      if (jump.toSectionId === null) continue;
      if (!ids.has(jump.toSectionId)) {
        errors.push({
          kind: "danglingTarget",
          sectionIds: [section.id, jump.toSectionId],
          message: `“${section.title}” jumps to a section that no longer exists.`,
        });
        continue;
      }
    }
    const fallback =
      section.defaultNextSectionId !== undefined
        ? section.defaultNextSectionId
        : (ordered[index + 1]?.id ?? null);
    if (fallback !== null) {
      if (!ids.has(fallback)) {
        errors.push({
          kind: "danglingTarget",
          sectionIds: [section.id, fallback],
          message: `“${section.title}” continues to a section that no longer exists.`,
        });
      }
    }
  }

  const cycle = findCycle(ordered[0]!.id, edges);
  if (cycle) {
    const titleOf = (id: string) =>
      ordered.find((s) => s.id === id)?.title ?? id;
    errors.push({
      kind: "cycle",
      sectionIds: cycle,
      message: `These sections can send an applicant round in a circle: ${cycle
        .map(titleOf)
        .map((t) => `“${t}”`)
        .join(" → ")}.`,
    });
  }

  const reachable = reachableFrom(ordered[0]!.id, edges);
  for (const section of ordered) {
    if (!reachable.has(section.id)) {
      warnings.push({
        kind: "unreachable",
        sectionIds: [section.id],
        message: `Nothing leads to “${section.title}”, so nobody will see it.`,
      });
    }
  }

  return { errors, warnings };
}

/**
 * The sections an applicant must pass through to arrive at `target`, on every
 * route the jumps allow.
 *
 * Ordering alone doesn't establish that a condition can read its source: a jump
 * can skip the section the source lives in, and on that route the answer is
 * simply absent — which makes `isEmpty` fire and `equals` never fire, quietly,
 * on some applicants and not others. A source is safe to read only if its
 * section is on every path.
 *
 * Standard iterative dominators over the section graph; forms are small enough
 * that the simple formulation is the right one.
 */
export function sectionsAlwaysVisitedBefore(
  form: FormContent,
  target: string,
): Set<string> {
  const ordered = orderedSections(form);
  const entry = ordered[0]?.id;
  if (!entry) return new Set();

  const ids = ordered.map((section) => section.id);
  const edges = sectionEdges(form);
  const predecessors = new Map<string, string[]>(ids.map((id) => [id, []]));
  for (const [from, targets] of edges) {
    for (const to of targets) predecessors.get(to)?.push(from);
  }

  // Everything dominates everything to begin with, except the entry, which only
  // dominates itself; then shrink until it settles.
  const dominators = new Map<string, Set<string>>(
    ids.map((id) => [id, new Set(id === entry ? [entry] : ids)]),
  );
  let changed = true;
  while (changed) {
    changed = false;
    for (const id of ids) {
      if (id === entry) continue;
      const preds = predecessors.get(id) ?? [];
      if (preds.length === 0) continue;
      let next = new Set<string>();
      let first = true;
      for (const pred of preds) {
        const predDominators = dominators.get(pred)!;
        if (first) {
          next = new Set(predDominators);
          first = false;
          continue;
        }
        next = new Set([...next].filter((x) => predDominators.has(x)));
      }
      next.add(id);
      const current = dominators.get(id)!;
      if (
        next.size !== current.size ||
        [...next].some((x) => !current.has(x))
      ) {
        dominators.set(id, next);
        changed = true;
      }
    }
  }

  const result = dominators.get(target) ?? new Set<string>();
  result.delete(target);
  return result;
}

/** Every section each section can hand the applicant to. */
/**
 * The sections every applicant passes through on the way to submitting,
 * whatever they answer: the ones no jump or "continue to" can route around.
 *
 * A question in any other section may never be asked of some applicants,
 * even with no `visibleWhen` of its own — the walk simply skipped its
 * section. Anything that needs an answer from *every* submission (a round
 * reading a required field, say) should check the question's section is in
 * this set, not just that the question has no condition.
 *
 * Computed by removal: a section is always visited iff, with it deleted from
 * the graph, submit can no longer be reached from the entry. Forms are small
 * enough that one reachability walk per section is the right formulation.
 *
 * Mirrors `walkOutcome`: a section with its own `visibleWhen` may be hidden,
 * and a hidden section doesn't steer — the walk carries on in document order
 * as though it weren't there. So such a section is never "always visited",
 * and the graph carries its document-order fall-through beside its
 * configured jumps and next.
 */
export function sectionsAlwaysVisited(form: FormContent): Set<string> {
  return new Set(
    form.sections
      .filter((section) => sectionsCoverEveryPath(form, new Set([section.id])))
      .map((section) => section.id),
  );
}

/**
 * Whether every possible route to submit visits at least one of these sections.
 * Removing the entire set detects coverage across alternative branches; testing
 * each section individually cannot. Conditional sections cannot guarantee coverage.
 * This is conservative: correlated jump predicates may admit infeasible paths.
 */
export function sectionsCoverEveryPath(
  form: FormContent,
  sectionIds: ReadonlySet<string>,
): boolean {
  const ordered = orderedSections(form);
  const entry = ordered[0]?.id;
  if (!entry) return false;
  const edges = sectionEdges(form);
  // A section from which the walk can end: an explicit jump to submit, a
  // configured next of "submit", or a fall-through with nothing after it.
  const terminal = new Set<string>();
  for (const [index, section] of ordered.entries()) {
    const inOrder = ordered[index + 1]?.id ?? null;
    if ((section.jumps ?? []).some((jump) => jump.toSectionId === null)) {
      terminal.add(section.id);
    }
    const fallback =
      section.defaultNextSectionId !== undefined
        ? section.defaultNextSectionId
        : inOrder;
    if (fallback === null) terminal.add(section.id);
    if (section.visibleWhen) {
      // Hidden: neither jumps nor the configured next apply.
      if (inOrder === null) terminal.add(section.id);
    }
  }

  const covered = new Set(
    ordered
      .filter((section) => sectionIds.has(section.id) && !section.visibleWhen)
      .map((section) => section.id),
  );
  if (covered.has(entry)) return true;
  const without = new Map(
    [...edges].map(([from, to]) => [
      from,
      to.filter((target) => !covered.has(target)),
    ]),
  );
  const reachable = reachableFrom(entry, without);
  return ![...reachable].some((id) => terminal.has(id));
}

/** Conservative overlap check: can one route reach both sections? */
export function sectionsMaySharePath(
  form: FormContent,
  left: string,
  right: string,
): boolean {
  const ordered = orderedSections(form);
  const entry = ordered[0]?.id;
  if (!entry) return false;
  const edges = sectionEdges(form);
  const reachable = reachableFrom(entry, edges);
  if (!reachable.has(left) || !reachable.has(right)) return false;
  return (
    reachableFrom(left, edges).has(right) ||
    reachableFrom(right, edges).has(left)
  );
}

/** All possible routes, including a hidden section's document-order bypass.
 * Publishing, dominance and mapping coverage must use the same graph as the
 * applicant walk: a hidden section ignores its configured jumps and next. */
function sectionEdges(form: FormContent): Map<string, string[]> {
  const ordered = orderedSections(form);
  const ids = new Set(ordered.map((section) => section.id));
  const edges = new Map<string, string[]>();
  for (const [index, section] of ordered.entries()) {
    const targets: string[] = [];
    for (const jump of section.jumps ?? []) {
      if (jump.toSectionId && ids.has(jump.toSectionId)) {
        targets.push(jump.toSectionId);
      }
    }
    const fallback =
      section.defaultNextSectionId !== undefined
        ? section.defaultNextSectionId
        : (ordered[index + 1]?.id ?? null);
    if (fallback !== null && ids.has(fallback)) targets.push(fallback);
    const next = ordered[index + 1];
    if (section.visibleWhen && next) targets.push(next.id);
    edges.set(section.id, targets);
  }
  return edges;
}

function reachableFrom(
  start: string,
  edges: ReadonlyMap<string, string[]>,
): Set<string> {
  const seen = new Set<string>([start]);
  const queue = [start];
  while (queue.length) {
    for (const next of edges.get(queue.shift()!) ?? []) {
      if (!seen.has(next)) {
        seen.add(next);
        queue.push(next);
      }
    }
  }
  return seen;
}

/** The first cycle reachable from `start`, as the loop's section ids. */
function findCycle(
  start: string,
  edges: ReadonlyMap<string, string[]>,
): string[] | null {
  const state = new Map<string, "visiting" | "done">();
  const stack: string[] = [];

  function visit(id: string): string[] | null {
    if (state.get(id) === "visiting") {
      return [...stack.slice(stack.indexOf(id)), id];
    }
    if (state.get(id) === "done") return null;
    state.set(id, "visiting");
    stack.push(id);
    for (const next of edges.get(id) ?? []) {
      const found = visit(next);
      if (found) return found;
    }
    stack.pop();
    state.set(id, "done");
    return null;
  }

  return visit(start);
}

/** The ids of every question the applicant was actually asked. */
export function askedQuestionIds(
  form: FormContent,
  answers: AnswerMap,
): Set<string> {
  return new Set(
    visibleQuestions(form, answers).map((question) => question.id),
  );
}
